import './styles.css';
import { fabric } from 'fabric';
import { createEditor } from './editor/editor';
import { createHistory } from './editor/history';
import { enter, showRelogin } from './auth/gate';
import { loadWork, saveErrorText, useAccount } from './editor/storage';
import { createSaver } from './sync/saver';
import { segmenters } from './thai/ThaiTextbox';
import { $ } from './ui/dom';
import { initAccountSheet } from './ui/accountSheet';
import { initAdminPanel } from './ui/adminPanel';
import { initDictSheet, renderDict, syncWords } from './ui/dictSheet';
import { initDocSheet } from './ui/docSheet';
import { initEditBar } from './ui/editbar';
import { initExportSheet } from './ui/exportSheet';
import { initLayersSheet } from './ui/layersSheet';
import { initMineSheet } from './ui/mineSheet';
import { initShareSheet } from './ui/shareSheet';
import { initSaveTemplateSheet } from './ui/saveTemplateSheet';
import { initTemplatesSheet } from './ui/templatesSheet';
import { api, canDo } from './sync/api';
import { initObjectTools, makeText } from './ui/objects';
import { initGraphicsPanel } from './ui/graphicsPanel';
import { initTextStyles } from './ui/textStyles';
import { initShapePanel } from './ui/shapePanel';
import { initTableView } from './ui/tableView';
import { initDocFill } from './ui/docFill';
import { initSlotFill } from './ui/slotFill';
import { initAiImage } from './ui/aiImage';
import { initAiTemplate } from './ui/aiTemplate';
import { initFlowSheet } from './ui/flowSheet';
import { createObjectActions } from './ui/objectActions';
import { initContextMenu } from './ui/contextMenu';
import { initPagesSheet } from './ui/pagesSheet';
import { initPageStrip } from './ui/pageStrip';
import { initDrawTools } from './ui/drawTools';
import { initRulers } from './ui/rulers';
import { initVideoView } from './ui/videoView';
import { initVideoStore } from './ui/videoStore';
import { initInstallApp } from './ui/installApp';
import { initSelBar } from './ui/selbar';
import { initGroupSelect } from './ui/groupSelect';
import { closeSheets, initSheets, isOpen, onSheetChange, openSheet, toggleSheet, whenOpened } from './ui/sheets';
import { toast } from './ui/toast';
import { initToolbar } from './ui/toolbar';
import { CORE_FONTS } from './fonts/catalog';
import { fitCanvasFonts, installFontFaces } from './fonts/load';
import { getWords } from './thai/dictionary';

/* ---------- header + view chips: document name, size, zoom ---------- */
function showView() {
  $('docName').textContent = ed.doc.name;
  $('sizeChip').textContent = `${ed.doc.w} × ${ed.doc.h} px${(ed.doc.bleedMm ?? 0) > 0 ? ` · ตัดตก ${ed.doc.bleedMm} mm` : ''}`;
  $('zoomChip').textContent = `${Math.round(ed.canvas.getZoom() * 100)}% · พอดีจอ`;
}

// the local script fonts and the Thai stand-in faces (src/fonts/load.ts), before anything is drawn
installFontFaces();
const ed = createEditor($<HTMLCanvasElement>('c'), $('stage'), showView);

/* ---------- history ---------- */
function updUndo() {
  ($('undo') as HTMLButtonElement).disabled = !history.canUndo();
  ($('redo') as HTMLButtonElement).disabled = !history.canRedo();
}
let fontToastShown = '';
/**
 * The page's fonts: loaded, its text measured again, and a font that could not come is SAID (BUG-78) — after a page
 * or work is opened (onRestored) and for the sample a new device starts with.
 */
function fitPageFonts() {
  const opened = saver.openedCount;
  void fitCanvasFonts(ed.canvas, ed.userObjects()).then(failed => {
    // ตาราง: drawn again with their real font (src/ui/tableView.ts)
    void tables.relayout();
    const key = `${opened}|${failed.join(',')}`;
    if (!failed.length || fontToastShown === key) return;   // once per work and fonts, not on every undo/page
    fontToastShown = key;
    toast(`โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตอนนี้ตัวหนังสือแสดงด้วยฟอนต์สำรอง ตรวจอินเทอร์เน็ตแล้วเปิดงานใหม่ (ถ้าเน็ตปกติ เครือข่ายนี้อาจบล็อก Google Fonts)`, true);
  });
}
const history = createHistory(ed, {
  onChange: () => { updUndo(); saver.changed(); strip.changed(); },
  onRestored: () => {
    saver.lockView(); layers.refresh(); onSelChange(); showView(); pages.sync(); strip.sync(); void video.sync();
    // a page/work may use a font that is not loaded yet (fonts load when used): load it, then measure its text again
    fitPageFonts();
  },
  onIncomplete: reason => saver.block(reason),
  onProblem: reason => toast(reason, true)
});
$('undo').onclick = () => history.undo();
$('redo').onclick = () => history.redo();

/* ---------- saving: this device first, then the server (src/sync/saver.ts) ---------- */
const saver = createSaver(ed, history, {
  onLoaded: () => { ed.fit(); showView(); layers.refresh(); updUndo(); onSelChange(); void video.sync(); docSheet.resetScope(); },
  onSignedOut: reason => { actions.clearClipboard(); showRelogin(reason, () => saver.saveLocal()); },
  onViewChange: v => {
    // someone else's design open read-only: no editing tools, a bar with "copy as mine" (src/ui/mineSheet.ts)
    document.querySelector('.app')!.classList.toggle('viewing', !!v);
    $('viewbar').hidden = !v;
    if (v) $('viewWho').textContent = `งานของ ${v.owner.name || v.owner.email}${v.owner.status === 'disabled' ? ' (ปิดการใช้งาน)' : ''} · ดูอย่างเดียว${v.admin ? ' (ผู้ดูแลแก้ไขได้)' : ''}`;
    $('viewEdit').hidden = !v?.admin;   // 0015 (2ข): an admin opens a member's work read-only first
    closeSheets(); showView(); updUndo(); strip.sync(); void video.sync();
  },
  // 0015: an admin is editing a member's work: the bar says whose, everywhere until "เสร็จแล้ว"
  onAdminEdit: owner => {
    $('adminbar').hidden = !owner;
    if (owner) $('adminWho').textContent = `กำลังแก้งานของ ${owner.name || owner.email} ในฐานะผู้ดูแล · บันทึกลงงานของเจ้าของ`;
  }
});

/* ---------- video projects (src/ui/videoView.ts): a video open covers the design canvas ---------- */
const video = initVideoView(ed, history, saver, {
  // admins, or members given the right (0011); the server checks every request too
  canPublish: () => canDo(account.account, 'templates'),
  can: p => canDo(account.account, p),
  isAdmin: () => account.account?.role === 'admin',
  publish: () => void saveTpl.open()
});

/* ---------- pages of the design (src/ui/pagesSheet.ts) ---------- */
const pages = initPagesSheet(ed, history, { canEdit: () => !saver.viewing });
// the strip of pages along the bottom and the open page's toolbar (src/ui/pageStrip.ts)
const strip = initPageStrip(ed, history, { canEdit: () => !saver.viewing });
// เครื่องมือ: pens, shapes, lines, notes, text (src/ui/drawTools.ts)
const tools = initDrawTools(ed, history, { canEdit: () => !saver.viewing });
const rulers = initRulers(ed, history, { canEdit: () => !saver.viewing, toolBusy: () => tools.mode !== 'select' });
// two fingers sideways (touch screen or touchpad): next/previous page (nothing past the first or last page)
ed.setOnSwipe(dir => pages.turn(dir));

/* ---------- panels, toolbar, selection bar ---------- */
const layers = initLayersSheet(ed, history, () => onSelChange());
const toolbar = initToolbar(ed, history, () => layers.refresh(), () => !saver.viewing, () => canDo(account.account, 'ai_image'));
// size and bleed changes are recorded by history.resizePages (one undo step each)
const docSheet = initDocSheet(ed, history, { onResized: () => { showView(); saver.changed(); }, clearAll: () => { const n = history.pageCount(); return { pages: n, error: history.clearAll() }; }, onBleed: () => { showView(); saver.changed(); } });
initSheets();
initDictSheet(ed, { myId: () => account.account?.id });
initEditBar(ed);
const actions = createObjectActions(ed, history, {
  canEdit: () => !saver.viewing, asBackground: () => toolbar.asBackground(),
  onBgChanged: () => { docSheet.sync(); saver.changed(); }, onArranged: () => layers.refresh()
});
initObjectTools(ed, history, actions);
// ช่องใส่รูปของเทมเพลต → "ใส่รูป" (src/ui/slotFill.ts)
initSlotFill(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null
});
// ช่องใส่รูป → "สร้างด้วย AI" (0022, src/ui/aiImage.ts)
initAiImage(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null,
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active'
});
// องค์ประกอบ → รูปทรง: 10 หมวด + "ดูทั้งหมด", พิมพ์ข้อความในรูปทรง (src/ui/shapePanel.ts)
const shapes = initShapePanel(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null
});
// องค์ประกอบ → ตาราง: แก้ตารางแบบ Excel (src/ui/tableView.ts) — before the line below that records object:modified, so
// a table pulled bigger is laid out again first
const tables = initTableView(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null
});
// องค์ประกอบ → กราฟิก (0019): the group's library + free sets; adding needs the right 'graphics' (the server checks too)
initGraphicsPanel(ed, {
  canEdit: () => !saver.viewing,
  canAdd: () => canDo(account.account, 'graphics'),
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active',
  shapes
});
// จัดกลุ่ม: a tap on a member chooses its whole group (src/ui/groupSelect.ts)
const groups = initGroupSelect(ed);
initContextMenu(ed, actions, groups, { canEdit: () => !saver.viewing });
initSelBar(ed, actions.groupState);

whenOpened('shLayers', layers.render);
whenOpened('shDict', () => { renderDict(); void syncWords(); });
whenOpened('shMore', () => { docSheet.sync(); rulers.syncSheet(); saver.render(); $('dTplRow').hidden = !canDo(account.account, 'templates'); flowSheet.showRow(); });
/* ---------- sharing (src/ui/shareSheet.ts, read-only bar) ---------- */
const share = initShareSheet({ onChanged: () => {} });
const videoStore = initVideoStore(ed, history, saver, { busy: () => video.busy(), refresh: () => void video.sync() });
const mine = initMineSheet(saver, {
  openShare: d => share.open(d), busy: () => video.busy(),
  videoKeys: id => videoStore.keysOf(id), dropCopies: (id, keys) => videoStore.dropCopies(id, keys),
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active'
});
$('dShare').onclick = async () => {
  const box = $('dShareMsg');
  box.hidden = true;
  const why = await saver.flush();   // a design must be on the server before it can be shared
  if (!saver.link.id || why) {
    box.textContent = `ยังแชร์ไม่ได้: ${why ?? 'งานนี้ยังไม่เคยขึ้นเซิร์ฟเวอร์ แก้ไขสักอย่างให้ระบบบันทึกขึ้นก่อน'}`;
    box.hidden = false; return;
  }
  share.open({ id: saver.link.id, name: ed.doc.name });
};
/* ---------- templates (src/ui/templatesSheet.ts, src/ui/saveTemplateSheet.ts) ---------- */
const templates = initTemplatesSheet(ed, history, saver, {
  busy: () => video.busy(),
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null,
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active',
  // a video template: its empty slots are the next thing to do
  onUsed: d => { if (d.kind === 'video') video.openSlots(); }
});
// เทมเพลต → "สร้างเทมเพลตด้วย AI" (0023, src/ui/aiTemplate.ts): the right "รูป AI" (the server checks too)
const aiTemplate = initAiTemplate(ed, history, saver, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null,
  busy: () => video.busy(),
  canUse: () => canDo(account.account, 'ai_image'),
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active'
});
whenOpened('shTemplates', () => { aiTemplate.showButton(); void templates.load(); });
// เพิ่มเติม → "กรอกข้อมูลเอกสาร" (src/ui/docFill.ts): ช่องข้อมูล · ภาษาเอกสาร · ข้อมูลบริษัท/แบรนด์ (0029)
initDocFill(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null
});
// เพิ่มเติม → "เขียน prompt สำหรับ Google Flow (AI)" (0025, src/ui/flowSheet.ts): the right "รูป AI" (the server checks too)
const flowSheet = initFlowSheet({
  canUse: () => canDo(account.account, 'ai_image'),
  online: () => saver.mode === 'online' ? null : saver.reason
});
$('mineNewVT').onclick = () => templates.openVideo();   // "+ วิดีโอจากเทมเพลต" in งานของฉัน
const saveTpl = initSaveTemplateSheet(ed, history, saver, {
  isAdmin: () => account.account?.role === 'admin',
  video: () => video.active ? { poster: () => video.poster(), lastExport: () => video.lastExport() } : null
});
$('dSaveTpl').onclick = () => void saveTpl.open();

$('viewCopy').onclick = async () => {
  const v = saver.viewing;
  if (!v) return;
  const btn = $('viewCopy') as HTMLButtonElement;
  btn.disabled = true;
  try {
    const { design } = await api.copy(v.id);
    const res = await api.get(design.id);
    await saver.exitViewTo(res.design, res.json);
    toast(`ทำสำเนาแล้ว ตอนนี้เปิด "${res.design.name}" ซึ่งเป็นงานของคุณ แก้ไขได้`);
  } catch (e) {
    toast('ทำสำเนาไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)), true);
  } finally { btn.disabled = false; }
};
$('viewClose').onclick = () => void saver.exitView().then(() => toast('กลับมาที่งานของคุณแล้ว'));
/* 0015 (2ข): from read-only to editing a member's work — opened again from the server, so it is the latest version */
$('viewEdit').onclick = async () => {
  const v = saver.viewing;
  if (!v?.admin) return;
  const btn = $('viewEdit') as HTMLButtonElement;
  btn.disabled = true;
  try {
    const res = await api.get(v.id);
    if (res.access !== 'admin' || !res.owner) throw new Error('บัญชีนี้ไม่ใช่ผู้ดูแลแล้ว หรืองานนี้กลายเป็นงานของคุณเอง');
    await saver.openAsAdmin(res.design, res.json, res.owner);
    toast(`แก้งานของ ${res.owner.name || res.owner.email} ได้แล้ว ทุกการแก้บันทึกลงงานของเจ้าของ`);
  } catch (e) {
    toast('เปิดแก้ไขไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)), true);
  } finally { btn.disabled = false; }
};
$('adminDone').onclick = async () => {
  const btn = $('adminDone') as HTMLButtonElement;
  const who = saver.editingFor;
  btn.disabled = true;
  try {
    const why = await saver.adminDone();
    if (why) toast(`${why} จึงยังไม่ปิด เพื่อไม่ให้การแก้ไขหาย`, true);
    else toast(`บันทึกงานของ ${who?.name || who?.email || 'สมาชิก'} แล้ว กลับมาที่งานของคุณ`);
  } finally { btn.disabled = false; }
};
whenOpened('shMine', () => void mine.render());
$('mineBtn').onclick = () => toggleSheet('shMine');
// แบบข้อความสำเร็จ (src/ui/textStyles.ts): the same guard as the object menu (someone else's work, a page loading)
const textStyles = initTextStyles(ed, history, {
  cannotEdit: () => saver.viewing ? 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null,
  // แบบของกลุ่ม (0021): admins, or members given the right (the server checks too)
  canSave: () => canDo(account.account, 'text_styles'),
  isAdmin: () => account.account?.role === 'admin' && account.account.status === 'active'
});
whenOpened('shText', () => { $('xDictCount').textContent = `· ${getWords().length} คำ`; textStyles.onOpen(); });
$('xDict').onclick = () => openSheet('shDict');
$('moreDict').onclick = () => openSheet('shDict');
$('docBtn').onclick = () => toggleSheet('shMore');
$('sizeChip').onclick = () => openSheet('shMore');
/* ---------- account + admin (src/ui/accountSheet.ts, src/ui/adminPanel.ts) ---------- */
const admin = initAdminPanel({ myId: () => account.account?.id, onChanged: () => void account.refresh() });
const account = initAccountSheet(saver, { openAdmin: () => { closeSheets(); admin.open(); } });
const install = initInstallApp();
whenOpened('shAccount', () => { void account.refresh(); install.paint(); });
// rights given or taken away by an admin (0011) show up when the person comes back to the app, without signing in again
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && account.account && navigator.onLine) void account.refresh(); });
$('acctBtn').onclick = () => toggleSheet('shAccount');
const exportSheet = initExportSheet(ed, history, { canEdit: () => !saver.viewing, setBleed: (mm, pages) => docSheet.setBleedOn(pages, mm) });
whenOpened('shExport', exportSheet.sync);
$('exportBtn').onclick = () => {
  if (video.active) { if (isOpen('shVExport')) closeSheets(); else video.openExport(); return; }
  ed.canvas.discardActiveObject(); ed.canvas.requestRenderAll(); toggleSheet('shExport');
};

/* tablets: a side panel covers the left of the work area, so fit the artboard into the space beside it */
const tablet = matchMedia('(min-width: 900px)');
const SIDE_PANEL_PX = 340;
// a panel opening puts the drawing tools away (the panel covers the rail on a tablet)
onSheetChange(open => { ed.setInsetLeft(open && tablet.matches ? SIDE_PANEL_PX : 0); if (open) tools.close(); });
tablet.addEventListener('change', () => ed.setInsetLeft(0));

/* phones: a selection swaps the tabs for the object's toolbar, so close any open panel */
function onSelChange() {
  if (ed.canvas.getActiveObject() && !matchMedia('(min-width: 900px)').matches) closeSheets();
  toolbar.onSelection();
  if (isOpen('shLayers')) layers.render();
}
(['selection:created', 'selection:updated', 'selection:cleared'] as const).forEach(ev => ed.canvas.on(ev, onSelChange));
ed.canvas.on('object:modified', () => toolbar.sync());

/* every edit is recorded */
(['object:added', 'object:removed', 'object:modified', 'text:changed', 'text:editing:exited'] as const)
  .forEach(ev => ed.canvas.on(ev, () => { history.push(); layers.refresh(); }));

/* ---------- start: fonts, then the last saved work or a sample ---------- */
function addSample() {
  const head = makeText('ปุ๋ยเกล็ดสูตรเร่งดอกทุเรียน', { fontFamily: 'Kanit', fontSize: 88, fontWeight: 700, fill: '#2F6B3A', width: 880, left: 100 });
  const body = makeText('สวนทุเรียนหมอนทองคุณภาพส่งออกปลอดภัยไร้สารตกค้างผ่านมาตรฐานGAPรับประกันเนื้อแห้งหวานมันทุกลูกสั่งจองล่วงหน้าได้แล้ววันนี้', { fontSize: 52, width: 880, left: 100 });
  const bar = new fabric.Rect({ width: 880, height: 16, rx: 8, ry: 8, fill: '#E0A526', left: 100, strokeWidth: 0, stroke: '#1A1E26', strokeUniform: true });
  bar.set({ name: 'แถบสี' } as Partial<fabric.Object>);
  let y = 110;
  for (const o of [head, body, bar] as fabric.Object[]) { o.set({ top: y }); o.setCoords(); y += o.getScaledHeight() + 48; }
  history.silently(() => ed.canvas.add(head, body, bar));
}

async function start() {
  // nothing opens until an approved account is signed in (src/auth/gate.ts)
  // the first nine fonts as before; the script fonts load when a work or the font sheet uses them
  const fonts = Promise.all(CORE_FONTS.flatMap(f => [400, 700].map(w => document.fonts.load(`${w} 40px "${f}"`, 'กขค').catch(() => null))));
  const entry = await enter();
  account.set(entry.account, entry.me);
  $('dTplRow').hidden = !canDo(entry.account, 'templates');   // admins, or members given the right (0011); the server checks too
  if (entry.offlineReason) toast(`ออฟไลน์ (${entry.offlineReason}) ใช้งานต่อได้ งานบันทึกในเครื่องนี้ และจะขึ้นเซิร์ฟเวอร์เมื่อกลับมาออนไลน์`, true);
  await fonts;
  ed.paint();
  // the group's no-break words before any text is laid out (keeps the remembered list when offline)
  if (!entry.offlineReason) await syncWords();
  window.addEventListener('online', () => void syncWords());

  let saved = null;
  try {
    if (await useAccount(entry.account.id)) toast('ย้ายงานที่เคยบันทึกในเครื่องนี้มาไว้ในบัญชีของคุณแล้ว');
    saved = await loadWork();
  }
  catch (e) {
    toast('เปิดที่เก็บข้อมูลในเครื่องไม่ได้ งานจะไม่ถูกบันทึกในเครื่อง: ' + saveErrorText(e), true);
  }

  if (saved) {
    await saver.loadInto(saved.json, saved.doc);
    // work saved before server saving existed has no link: mark it so it goes up to the server once
    const hasObjects = ed.userObjects().length > 0;
    saver.setLink(saved.server ?? { id: null, version: 0, dirty: hasObjects });
    saver.setLocalAt(new Date(saved.savedAt));
    await saver.checkRestored(saved.json);   // หน้าหลายขนาด: work an old app left unsaved must not flatten the server copy
  } else {
    addSample(); ed.fit(); layers.refresh();
    history.reset(); updUndo();
    fitPageFonts();
  }
  showView();
  await saver.start();
  if (!segmenters) toast('เบราว์เซอร์นี้ไม่รองรับการตัดคำภาษาไทย ข้อความจะตัดบรรทัดทีละตัวอักษร กรุณาอัปเดตเบราว์เซอร์', true);
}

start();

/* test hook used by the browser smoke test */
declare global { interface Window { __craft?: unknown } }
window.__craft = { ed, history, saver };
